import { useState, useEffect, useRef, useCallback } from 'react';
import { toast, Toaster } from 'sonner';
import { QrCode, Gift, Loader2, LogOut, PartyPopper, Camera, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

function ScanLogin({ onSuccess }) {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      if (isLogin) {
        const r = await fetch(`${API}/api/auth/login`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
        });
        const data = await r.json();
        if (!r.ok) { setError(data.detail || 'Erreur'); setLoading(false); return; }
        if (data.user?.role !== 'scan' && data.user?.role !== 'patron') {
          setError("Ce compte n'a pas acces au Scan"); setLoading(false); return;
        }
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('scan_user', JSON.stringify(data.user));
        onSuccess(data.user);
      } else {
        const r = await fetch(`${API}/api/auth/register`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.trim().toLowerCase(), password, name, role: 'scan' }),
        });
        const data = await r.json();
        if (!r.ok) { setError(data.detail || 'Erreur'); setLoading(false); return; }
        toast.success('Inscription envoyee ! Le patron doit valider votre acces.');
        setIsLogin(true);
      }
    } catch { setError('Erreur de connexion'); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0f172a] to-[#1e293b] flex items-center justify-center p-4" data-testid="scan-login">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-emerald-500/20 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <QrCode size={28} className="text-emerald-400" />
          </div>
          <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Georgia, serif', letterSpacing: '0.1em' }}>LE MAS</h1>
          <p className="text-xs text-gray-400 tracking-widest uppercase mt-1">Espace Scan</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 space-y-4">
          {!isLogin && (
            <div>
              <label className="text-[10px] text-gray-400 uppercase tracking-wider">Prenom & Nom</label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Jean Dupont" required
                className="bg-white/5 border-white/10 text-white mt-1" data-testid="scan-name" />
            </div>
          )}
          <div>
            <label className="text-[10px] text-gray-400 uppercase tracking-wider">Email</label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@example.com" required
              className="bg-white/5 border-white/10 text-white mt-1" data-testid="scan-email" />
          </div>
          <div>
            <label className="text-[10px] text-gray-400 uppercase tracking-wider">Mot de passe</label>
            <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="******" required minLength={6}
              className="bg-white/5 border-white/10 text-white mt-1" data-testid="scan-password" />
          </div>
          {error && <p className="text-xs text-red-400" data-testid="scan-error">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-11" data-testid="scan-submit">
            {loading ? <Loader2 size={16} className="animate-spin" /> : isLogin ? 'Se connecter' : "S'inscrire"}
          </Button>
          <button type="button" onClick={() => { setIsLogin(!isLogin); setError(''); }}
            className="w-full text-center text-xs text-gray-400 hover:text-white transition-colors" data-testid="scan-toggle">
            {isLogin ? "Pas encore de compte ? S'inscrire" : 'Deja un compte ? Se connecter'}
          </button>
        </form>
      </div>
    </div>
  );
}

function QrScanner({ onScan, onClose }) {
  const videoRef = useRef(null);
  const scannerRef = useRef(null);

  useEffect(() => {
    let html5QrCode;
    const startScanner = async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        html5QrCode = new Html5Qrcode("qr-reader");
        scannerRef.current = html5QrCode;
        await html5QrCode.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decodedText) => {
            onScan(decodedText);
            html5QrCode.stop().catch(() => {});
          },
          () => {}
        );
      } catch (err) {
        console.error("Scanner error:", err);
        toast.error("Impossible d'acceder a la camera");
      }
    };
    startScanner();
    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
      }
    };
  }, [onScan]);

  return (
    <div className="space-y-3" data-testid="qr-scanner">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-[rgb(var(--lm-text))]">Scanner QR Code</h3>
        <button onClick={onClose} className="text-xs text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]">Fermer</button>
      </div>
      <div id="qr-reader" className="rounded-xl overflow-hidden" style={{ width: '100%' }} />
      <p className="text-[10px] text-center text-[rgb(var(--lm-text-muted))]">Pointez la camera vers le QR code</p>
    </div>
  );
}

export default function ScanApp() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [activeTab, setActiveTab] = useState('home');
  const [showScanner, setShowScanner] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [verifyResult, setVerifyResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [recentScans, setRecentScans] = useState([]);

  useEffect(() => {
    const saved = localStorage.getItem('scan_user');
    if (saved) { try { setUser(JSON.parse(saved)); } catch {} }
    setChecking(false);
  }, []);

  const loadRecentScans = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/scan/logs?limit=20`);
      if (r.ok) setRecentScans(await r.json());
    } catch {}
  }, []);

  useEffect(() => { if (user) loadRecentScans(); }, [user, loadRecentScans]);

  const handleLogout = () => { localStorage.removeItem('auth_token'); localStorage.removeItem('scan_user'); setUser(null); };

  const verifyCode = async (code) => {
    if (!code?.trim()) return;
    setLoading(true); setVerifyResult(null); setShowScanner(false);
    try {
      const r = await fetch(`${API}/api/scan/verify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim() }),
      });
      const data = await r.json();
      setVerifyResult(data);
      if (!data.found) toast.error('Code non reconnu');
    } catch { toast.error('Erreur connexion'); }
    setLoading(false);
  };

  const validateCode = async () => {
    if (!verifyResult?.found) return;
    setValidating(true);
    try {
      const r = await fetch(`${API}/api/scan/validate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: verifyResult.data.numero || verifyResult.data.code,
          type: verifyResult.type,
          scanner_name: user?.nom || user?.name || user?.email || 'Employe',
        }),
      });
      const data = await r.json();
      if (r.ok) {
        toast.success(data.message);
        setVerifyResult(null);
        setManualCode('');
        loadRecentScans();
      } else {
        toast.error(data.detail || 'Erreur');
      }
    } catch { toast.error('Erreur'); }
    setValidating(false);
  };

  if (checking) return <div className="min-h-screen bg-[#0f172a] flex items-center justify-center"><Loader2 className="animate-spin text-emerald-400" /></div>;
  if (!user) return <><ScanLogin onSuccess={setUser} /><Toaster position="top-center" richColors /></>;

  return (
    <div className="min-h-screen bg-[rgb(var(--lm-bg))]" data-testid="scan-app">
      <header className="sticky top-0 z-40 bg-[rgb(var(--lm-card))]/95 backdrop-blur-md border-b border-[rgb(var(--lm-border))]/30">
        <div className="max-w-lg mx-auto flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <QrCode size={18} className="text-emerald-500" />
            <span className="text-base font-bold text-[rgb(var(--lm-text))]" style={{ fontFamily: 'Georgia, serif', letterSpacing: '0.08em' }}>LE MAS</span>
            <span className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase">Scan</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[9px] text-[rgb(var(--lm-text-muted))]">{user.nom || user.email}</span>
            <button onClick={handleLogout} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-red-500/10" data-testid="scan-logout">
              <LogOut size={14} className="text-[rgb(var(--lm-text-muted))]" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto p-4 space-y-4">
        {/* Scanner button */}
        <Button onClick={() => { setShowScanner(true); setVerifyResult(null); }}
          className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-14 text-base gap-3 rounded-xl" data-testid="open-scanner">
          <Camera size={22} /> Scanner un QR code
        </Button>

        {/* Camera scanner */}
        {showScanner && (
          <QrScanner onScan={verifyCode} onClose={() => setShowScanner(false)} />
        )}

        {/* Manual code entry */}
        <div className="flex gap-2">
          <Input value={manualCode} onChange={e => setManualCode(e.target.value.toUpperCase())}
            placeholder="Ou entrer le code manuellement..." className="flex-1 uppercase text-sm"
            onKeyDown={e => e.key === 'Enter' && verifyCode(manualCode)} data-testid="manual-code" />
          <Button onClick={() => verifyCode(manualCode)} disabled={loading || !manualCode.trim()}
            className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-navy))]" data-testid="verify-btn">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <QrCode size={16} />}
          </Button>
        </div>

        {/* Verification result */}
        {verifyResult && (
          <div className={`rounded-xl border-2 overflow-hidden ${verifyResult.found ? 'border-emerald-500/30' : 'border-red-500/30'}`} data-testid="scan-result">
            {!verifyResult.found ? (
              <div className="p-4 bg-red-50 dark:bg-red-900/10 text-center">
                <XCircle size={32} className="text-red-400 mx-auto mb-2" />
                <p className="text-sm font-bold text-red-600">Code non reconnu</p>
                <p className="text-xs text-red-400 mt-1">Verifiez le code et reessayez</p>
              </div>
            ) : (
              <div className="bg-[rgb(var(--lm-card))]">
                {/* Header */}
                <div className={`px-4 py-3 flex items-center gap-3 ${verifyResult.type === 'bon_cadeau' ? 'bg-emerald-500/10' : 'bg-amber-500/10'}`}>
                  {verifyResult.type === 'bon_cadeau' ? (
                    <Gift size={24} className="text-emerald-500" />
                  ) : (
                    <PartyPopper size={24} className="text-amber-500" />
                  )}
                  <div>
                    <p className="text-sm font-bold text-[rgb(var(--lm-text))]">
                      {verifyResult.type === 'bon_cadeau' ? 'Bon Cadeau' : 'Lot Roue du Mas'}
                    </p>
                    <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">
                      {verifyResult.data.numero || verifyResult.data.code}
                    </p>
                  </div>
                </div>

                {/* Details */}
                <div className="p-4 space-y-2">
                  {verifyResult.type === 'bon_cadeau' && (
                    <>
                      <div className="text-center py-2">
                        <p className="text-3xl font-bold text-emerald-500">{verifyResult.data.montant}EUR</p>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><span className="text-[rgb(var(--lm-text-muted))]">Client:</span> <strong>{verifyResult.data.client_nom || '—'}</strong></div>
                        <div><span className="text-[rgb(var(--lm-text-muted))]">Pour:</span> <strong>{verifyResult.data.beneficiaire || '—'}</strong></div>
                        <div><span className="text-[rgb(var(--lm-text-muted))]">Expire:</span> <strong>{verifyResult.data.date_expiration || '—'}</strong></div>
                        <div><span className="text-[rgb(var(--lm-text-muted))]">Statut:</span>
                          <strong className={verifyResult.data.statut === 'UTILISE' ? 'text-red-500 ml-1' : verifyResult.data.statut === 'EXPIRE' ? 'text-gray-500 ml-1' : 'text-emerald-500 ml-1'}>
                            {verifyResult.data.statut}
                          </strong>
                        </div>
                      </div>
                    </>
                  )}
                  {verifyResult.type === 'roue_voucher' && (
                    <>
                      <div className="text-center py-2">
                        <p className="text-2xl">{verifyResult.data.prize_emoji}</p>
                        <p className="text-lg font-bold text-amber-500">{verifyResult.data.prize_label}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><span className="text-[rgb(var(--lm-text-muted))]">Client:</span> <strong>{verifyResult.data.client_nom || '—'}</strong></div>
                        <div><span className="text-[rgb(var(--lm-text-muted))]">Statut:</span>
                          <strong className={verifyResult.data.used ? 'text-red-500 ml-1' : 'text-emerald-500 ml-1'}>
                            {verifyResult.data.used ? `Utilise ${(verifyResult.data.used_at || '').slice(0,10)}` : 'Valide'}
                          </strong>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Validate button */}
                  {((verifyResult.type === 'bon_cadeau' && verifyResult.data.statut !== 'UTILISE' && verifyResult.data.statut !== 'EXPIRE') ||
                    (verifyResult.type === 'roue_voucher' && !verifyResult.data.used)) && (
                    <Button onClick={validateCode} disabled={validating}
                      className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-12 text-sm mt-3 rounded-xl" data-testid="validate-btn">
                      {validating ? <Loader2 size={16} className="animate-spin mr-2" /> : <CheckCircle2 size={16} className="mr-2" />}
                      Valider / Utiliser
                    </Button>
                  )}
                  {verifyResult.type === 'bon_cadeau' && verifyResult.data.statut === 'UTILISE' && (
                    <div className="bg-red-50 dark:bg-red-900/10 rounded-lg p-3 text-center">
                      <p className="text-xs text-red-500 font-bold">Deja utilise le {verifyResult.data.date_utilisation}</p>
                    </div>
                  )}
                  {verifyResult.type === 'roue_voucher' && verifyResult.data.used && (
                    <div className="bg-red-50 dark:bg-red-900/10 rounded-lg p-3 text-center">
                      <p className="text-xs text-red-500 font-bold">Deja utilise le {(verifyResult.data.used_at || '').slice(0,10)}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Quick links */}
        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => window.open('/roue', '_self')}
            className="bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))]/30 rounded-xl p-4 text-center hover:shadow-md transition-all"
            data-testid="goto-roue">
            <PartyPopper size={24} className="text-amber-500 mx-auto mb-2" />
            <p className="text-xs font-bold text-[rgb(var(--lm-text))]">Roue du Mas</p>
            <p className="text-[8px] text-[rgb(var(--lm-text-muted))]">Lancer le jeu</p>
          </button>
          <button onClick={() => setActiveTab(activeTab === 'history' ? 'home' : 'history')}
            className="bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))]/30 rounded-xl p-4 text-center hover:shadow-md transition-all"
            data-testid="goto-history">
            <Clock size={24} className="text-blue-500 mx-auto mb-2" />
            <p className="text-xs font-bold text-[rgb(var(--lm-text))]">Historique</p>
            <p className="text-[8px] text-[rgb(var(--lm-text-muted))]">{recentScans.length} scan(s)</p>
          </button>
        </div>

        {/* Recent scans */}
        {activeTab === 'history' && (
          <div className="space-y-1.5" data-testid="scan-history">
            <p className="text-[10px] font-bold text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Scans recents</p>
            {recentScans.length === 0 ? (
              <p className="text-xs text-[rgb(var(--lm-text-muted))] text-center py-6">Aucun scan</p>
            ) : recentScans.map((s, i) => (
              <div key={i} className="bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))]/20 rounded-lg px-3 py-2 flex items-center gap-3">
                {s.type === 'bon_cadeau' ? <Gift size={14} className="text-emerald-500 shrink-0" /> : <PartyPopper size={14} className="text-amber-500 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold text-[rgb(var(--lm-text))] truncate">
                    {s.type === 'bon_cadeau' ? `${s.code} — ${s.montant}EUR` : `${s.prize || s.code}`}
                  </p>
                  <p className="text-[8px] text-[rgb(var(--lm-text-muted))]">{s.client || '?'} — par {s.scanner}</p>
                </div>
                <span className="text-[8px] text-[rgb(var(--lm-text-muted))] shrink-0">
                  {new Date(s.timestamp).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            ))}
          </div>
        )}
      </main>
      <Toaster position="top-center" richColors />
    </div>
  );
}
